import { ref } from 'vue'

import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-partnerships/mobile-plan/types/mobile-plan-offers'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import type { ReplacementOption } from '../api/cart/types'

export type ReplaceItemParams = {
  original: ReplacementOption
  replacement: ReplacementOption
  mobilePlan?: { selectedOffer: MobilePlanOffer } | null
  categoryId: number
  trackingContext: {
    index: number
    list: string
  }
}

export function useItemReplacement() {
  const $httpFetch = useHttpFetch()
  const logger = useLogger()
  const { trackAddToCartGa4, trackReplaceInCart, trackErrors } = useTracking()
  const { openErrorToast } = useTheToast()
  const { countryCode } = useMarketplace().market

  const isReplacing = ref(false)

  async function replaceItem(params: ReplaceItemParams): Promise<void> {
    const { original, replacement, mobilePlan, categoryId, trackingContext } =
      params

    try {
      isReplacing.value = true

      // We cannot parallelize those calls due to race conditions
      await $httpFetch('/bm/cart/add_product', {
        method: 'POST',
        body: {
          listing_id: replacement.listingId,
        },
      })

      await $httpFetch('/bm/cart/update_quantity', {
        method: 'POST',
        body: {
          action: 'delete',
          listing_id: `${original.listingId}`,
          listing_price: parseInt(original.price.amount, 10),
          new_quantity: 0,
        },
      })

      if (mobilePlan?.selectedOffer) {
        await $httpFetch('/bm/cart/services', {
          method: 'POST',
          body: {
            type: 'MOBILE_PLAN',
            listingPublicId: replacement.listingPublicId,
            offerId: mobilePlan.selectedOffer.id,
          },
        })
      }

      // Auto-replacements swap the listing entirely, so the price change
      // isn't visible anywhere else (unlike a same-listing price update,
      // which the backend already surfaces via CartItem.previousPrice).
      const autoReplacePriceChanged =
        trackingContext.list === 'cart_item_auto_replace' &&
        original.price.amount !== replacement.price.amount

      trackReplaceInCart({
        list: trackingContext.list,
        category: categoryId,
        listingId: replacement.listingId,
        name: replacement.title,
        price: replacement.price.amount,
        currency: replacement.price.currency,
        merchantId: replacement.sellerPublicId,
        position: trackingContext.index,
        quantity: 1,
        isOutOfStock: false,
        variant: replacement.grade.value,
        specialOfferType: replacement.offerType.value,
        previousPrice: autoReplacePriceChanged ? original.price.amount : '',
        // We don't have those informations in the in-store flow. We should add them in
        // the replacement endpoint: https://github.com/BackMarket/checkout/pull/566
        // id: props.item.productId,
        // uuid: props.item.akeneoId,
        // brand: props.item.brand,
      })
      /* eslint-disable camelcase */
      const price = parseFloat(replacement.price.amount)
      trackAddToCartGa4({
        zone: trackingContext.list,
        currency: replacement.price.currency,
        value: price,
        items: [
          {
            item_id: replacement.listingId,
            item_uuid: replacement.listingPublicId,
            item_name: replacement.title,
            item_category: categoryId != null ? String(categoryId) : undefined,
            price,
            quantity: 1,
            affiliation: replacement.sellerPublicId,
            index: trackingContext.index,
            item_list_id: trackingContext.list,
            item_list_name: trackingContext.list,
            item_variant: replacement.grade.value,
            location_id: countryCode,
            google_business_vertical: 'retail',
          },
        ],
      })
      /* eslint-enable camelcase */
    } catch (error) {
      logger.error(
        '[CHECKOUT][OOS Alternative] An error occured while replacing a product',
        {
          owners: ['bot-squad-checkout-front'],
          error: error as unknown as Error,
        },
      )

      trackErrors({
        zone: 'oos_replacement',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        message: (error as any)?.payload?.detail ?? 'unknown_error',
      })

      openErrorToast()

      throw error
    } finally {
      isReplacing.value = false
    }
  }

  return {
    replaceItem,
    isReplacing,
  }
}
